
import { defineComponent, ref, onMounted, computed } from "vue"
import { Actions } from "@/store/enums/StoreEnums"
import { useStore } from "vuex"
import { useRoute, useRouter } from "vue-router"
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb"
import { ElMessage } from "element-plus"
import { Plus } from '@element-plus/icons-vue'
import type { ElForm } from "element-plus"

type FormInstance = InstanceType<typeof ElForm>;

interface CardSeriesForm {
  name: string;
  image: string | null;
  description: string | null;
  enabled: boolean;
  sort: number;
}

export default defineComponent({
  components: {
    Plus
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const formRef = ref<FormInstance>()

    const isNew = !route.params.id

    onMounted(() => {
      if (isNew) {
        setCurrentPageBreadcrumbs("新建卡牌系列", ["抽卡系统", "卡牌系列;card_series"])
      } else {
        setCurrentPageBreadcrumbs("编辑卡牌系列", ["抽卡系统", "卡牌系列;card_series"])
      }
    })

    const cardSeries = ref<CardSeriesForm>({
      name: '',
      image: null,
      description: null,
      enabled: true,
      sort: 0
    })

    const rules = ref({
      name: [
        { required: true, message: '请输入系列名称', trigger: 'blur' },
        { min: 1, max: 50, message: '名称长度需在 1-50 之间', trigger: 'blur' }
      ]
    })

    // 图片上传相关（base64 方式，随表单提交）
    const imageUrl = ref('')

    const onImageChange = (file: { raw: File }) => {
      const rawFile = file.raw
      if (!rawFile.type.startsWith('image/')) {
        ElMessage.error('只能上传图片文件!')
        return
      }
      if (rawFile.size / 1024 / 1024 > 2) {
        ElMessage.error('图片大小不能超过 2MB!')
        return
      }
      const reader = new FileReader()
      reader.readAsDataURL(rawFile)
      reader.onload = (e) => {
        imageUrl.value = e.target?.result?.toString() || ''
        cardSeries.value.image = imageUrl.value
      }
    }

    // 加载详情（编辑模式）
    if (!isNew) {
      store
        .dispatch(Actions.GET_CARD_SERIES, route.params.id)
        .then(() => {
          const data = store.getters.currentCardSeries
          cardSeries.value = {
            name: data.name,
            image: data.image,
            description: data.description,
            enabled: data.enabled,
            sort: data.sort || 0
          }
          if (data.image) {
            imageUrl.value = data.image
          }
        })
        .catch(() => {
          ElMessage.error('获取系列详情失败')
        })
    }

    const onSubmit = (formEl: FormInstance | undefined) => {
      if (!formEl) return

      formEl.validate((valid) => {
        if (valid) {
          const params = { ...cardSeries.value }

          if (isNew) {
            store
              .dispatch(Actions.CREATE_CARD_SERIES, params)
              .then((data: { success: boolean; error_msg?: string }) => {
                if (data.success) {
                  ElMessage.success('创建成功')
                  router.push({ name: 'card_series' })
                } else {
                  ElMessage.error(data.error_msg || '创建失败')
                }
              })
              .catch(() => {
                ElMessage.error('创建失败')
              })
          } else {
            store
              .dispatch(Actions.UPDATE_CARD_SERIES, {
                id: route.params.id,
                values: params
              })
              .then((data: { success: boolean; error_msg?: string }) => {
                if (data.success) {
                  ElMessage.success('更新成功')
                  router.push({ name: 'card_series' })
                } else {
                  ElMessage.error(data.error_msg || '更新失败')
                }
              })
              .catch(() => {
                ElMessage.error('更新失败')
              })
          }
        }
      })
    }

    const onCancel = () => {
      router.push({ name: 'card_series' })
    }

    return {
      formRef,
      cardSeries,
      rules,
      isNew,
      imageUrl,
      onImageChange,
      onSubmit,
      onCancel
    }
  }
})
